/**
 * Tipos para o Roteamento Dinâmico de Pagamentos (PaymentHub).
 *
 * O roteamento decide qual empresa do grupo captura cada pagamento. Dois modos:
 *  - 0 = prioridade por teto (empresa com teto tem prioridade; a padrão absorve o resto);
 *  - 1 = balanceamento por peso (rodízio por VALOR capturado no mês, teto que nunca é
 *        ultrapassado e bloqueio de transportadora/serviço por empresa; sem empresa padrão).
 */

/** 0 = prioridade por teto (atual), 1 = balanceamento por peso. */
export type PaymentRoutingMode = 0 | 1;

export const ROUTING_MODE_CAP_PRIORITY: PaymentRoutingMode = 0;
export const ROUTING_MODE_WEIGHTED: PaymentRoutingMode = 1;

/**
 * Transportadora/serviço bloqueado para a empresa ([paymenthub].[PaymentRoutingCarrierBlock]).
 * provider_service_id null = todos os serviços da transportadora.
 */
export interface PaymentRoutingCarrierBlock {
  provider_id: number;
  provider_service_id: number | null;
  provider_name?: string;
  provider_service_name?: string | null;
}

// ===== Leitura (GET/PUT response) =====

export interface PaymentRoutingCity {
  payment_routing_city_id: number;
  city_name: string;
  state_uf: string | null;
}

export interface PaymentRoutingRule {
  payment_routing_rule_id: number;
  company_id: number;
  company_name: string;
  company_document: string;
  is_default: boolean;
  enabled: boolean;
  monthly_revenue_cap: number | null;
  cap_trigger_percent: number;
  /** Mês em que o teto foi atingido, formato 'YYYY-MM'. */
  cap_reached_month: string | null;
  cap_reached_on: string | null;
  cities: PaymentRoutingCity[];
  /** Peso do balanceamento (1..100). null quando nunca configurado. */
  weight?: number | null;
  carrier_blocks?: PaymentRoutingCarrierBlock[];
  /** Capturado no mês corrente (reais) — PIX pago + cartão capturado. */
  captured_this_month?: number;
}

export interface PaymentRoutingConfig {
  routing_enabled: boolean;
  routing_mode?: PaymentRoutingMode;
  rules: PaymentRoutingRule[];
}

/** Envelope `data` retornado pelo GET e pelo PUT. */
export interface PaymentRoutingConfigResult {
  config: PaymentRoutingConfig;
  warnings: string[];
}

// ===== Escrita (PUT payload) =====

export interface PaymentRoutingCityPayload {
  city_name: string;
  state_uf: string | null;
}

export interface PaymentRoutingRulePayload {
  company_id: number;
  is_default: boolean;
  enabled: boolean;
  monthly_revenue_cap: number | null;
  cap_trigger_percent: number;
  cities: PaymentRoutingCityPayload[];
  weight: number | null;
  carrier_blocks: { provider_id: number; provider_service_id: number | null }[];
}

export interface PaymentRoutingConfigPayload {
  routing_enabled: boolean;
  routing_mode: PaymentRoutingMode;
  rules: PaymentRoutingRulePayload[];
}

// ===== Transportadoras/serviços (lista para o bloqueio) =====

export interface CarrierServiceOption {
  provider_id: number;
  provider_name: string;
  provider_service_id: number;
  provider_service_name: string;
  /** Rótulo exibido ao cliente no site/CRM (ex.: "Expresso Aereo"); vazio se nunca cotado. */
  customer_label?: string | null;
  /** Nº de cotações nos últimos 90 dias. */
  quotes_90d?: number;
  /** Observação (ex.: "despachado pelo contrato próprio dos Correios"). */
  note?: string | null;
}

// ===== Simulador =====

export type RoutingPaymentKind = 'pix' | 'card';

export interface SimulateRoutingPayload {
  amount: number;
  sales_channel_id: number; // 1 = site, 2 = CRM
  shipping_provider_id: number | null;
  shipping_provider_service_id: number | null;
  delivery_city: string;
  delivery_state: string;
  payment_kind: RoutingPaymentKind;
}

export interface SimulateRoutingCompany {
  company_id: number;
  company_name: string;
  weight: number;
  /** Teto efetivo (teto × % de disparo); 0 = sem teto. */
  cap: number;
  captured: number;
  enabled: boolean;
  acquirer_ok: boolean;
  city_ok: boolean;
  carrier_blocked: boolean;
  fits_cap: boolean;
  ratio: number;
}

export interface SimulateRoutingResult {
  company_id: number;
  company_name: string;
  reason: string;
  forced: boolean;
  config_error: string;
  /** Aviso do PaymentHub: recebido do mês parcial/indisponível, modo inativo. */
  message?: string;
  routing_mode: PaymentRoutingMode;
  companies: SimulateRoutingCompany[];
}

// ===== Decisões registradas =====

export interface PaymentRoutingDecision {
  payment_routing_decision_id: number;
  created_on: string;
  sales_channel_id: number;
  order_id: number | null;
  comercial_proposal_id: number | null;
  payment_kind: string;
  amount: number;
  provider_name: string | null;
  provider_service: string | null;
  chosen_company_id: number;
  chosen_company_name: string;
  reason: string;
  forced: boolean;
}

export interface PaymentRoutingDecisionQuery {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  company_id?: number;
  page: number;
  page_size: number;
}

export interface PaymentRoutingDecisionPage {
  items: PaymentRoutingDecision[];
  total: number;
  page: number;
  page_size: number;
}

// ===== Bloqueio de transportadora (telas de logística) =====

/** GET /shipping/carrier-blocks?company_id=X */
export interface CompanyCarrierBlock {
  provider_id: number;
  provider_service_id: number | null;
  reason: string;
}
